import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, SIZES, fmt, fmtInt, resolveImage, formatApiError } from "@/lib/api";
import { Pencil, Trash2, Plus, X, Search, PackagePlus, History, Activity } from "lucide-react";
import Pagination from "@/components/Pagination";

const blankProduct = () => ({
  style_id: "", name: "", fabric: "", cost_price: 0, selling_price: 0, image_path: null,
  colors: [], cutting_quantity: "",
});

export default function Products() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [restocking, setRestocking] = useState(null);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = () => api.get("/products").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`); load();
  };

  const totalQty = (p) => p.colors.reduce((sum, c) => sum + SIZES.reduce((a, s) => a + (c.sizes[s] || 0), 0), 0);

  // Search first over the full list, then sort ascending by style ID — pagination below
  // only slices what's already been filtered, so search always covers everything.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = !q ? items : items.filter((p) =>
      p.style_id.toLowerCase().includes(q) ||
      p.name.toLowerCase().includes(q) ||
      (p.fabric || "").toLowerCase().includes(q) ||
      p.colors.some((c) => c.color.toLowerCase().includes(q))
    );
    return [...base].sort((a, b) => (a.style_id || "").toLowerCase().localeCompare((b.style_id || "").toLowerCase()));
  }, [items, query]);

  useEffect(() => { setPage(1); }, [query, pageSize]);
  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  return (
    <div data-testid="products-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Products</h1>
          <p className="page-sub">{filtered.length} of {items.length} product(s)</p>
        </div>
        <div className="toolbar">
          <div className="search-wrap" style={{ width: 280 }}>
            <Search size={16}/>
            <input data-testid="product-search" className="input" placeholder="Search style, name, fabric, color..." value={query} onChange={(e) => setQuery(e.target.value)}/>
          </div>
          <button className="btn btn-primary" data-testid="add-product-btn" onClick={() => { setErr(""); setEditing(blankProduct()); }}>
            <Plus size={16}/> Add Product
          </button>
        </div>
      </div>

      <div className="card-flush" data-testid="products-table">
        <table className="tbl">
        <thead><tr><th></th><th>Style ID</th><th>Name</th><th>Fabric</th><th className="num">Cost</th><th className="num">Selling</th><th>Colors</th><th className="num">Cutting Qty</th><th className="num">Listed Qty</th><th className="num">Available Qty</th><th></th></tr></thead>
          <tbody>
          {filtered.length === 0 && <tr><td colSpan={11} style={{ textAlign: "center", color: "var(--text-muted)", padding: 32 }}>{items.length === 0 ? "No products yet. Click \u201CAdd Product\u201D to start." : "No products match your search."}</td></tr>}
            {paged.map((p) => (
              <tr key={p.id} data-testid={`product-row-${p.style_id}`}>
                <td>{p.image_path ? <img className="thumb" src={resolveImage(p.image_path)} alt=""/> : <div className="thumb"/>}</td>
                <td><strong>{p.style_id}</strong></td>
                <td>{p.name}</td>
                <td style={{ color: "var(--text-muted)" }}>{p.fabric || "—"}</td>
                <td className="num">₹ {fmt(p.cost_price)}</td>
                <td className="num">₹ {fmt(p.selling_price)}</td>
                <td>{p.colors.map((c) => <span key={c.color} className="badge badge-mute" style={{ marginRight: 4 }}>{c.color}</span>)}</td>
                <td className="num">{p.cutting_quantity != null ? fmtInt(p.cutting_quantity) : "—"}</td>
                <td className="num">
                  {p.listed_quantity != null ? fmtInt(p.listed_quantity) : "—"}
                  {p.cutting_quantity != null && p.listed_quantity != null && p.listed_quantity !== p.cutting_quantity && (
                    <span
                      title={`Cutting qty (${p.cutting_quantity}) doesn't match listed qty (${p.listed_quantity})`}
                      style={{ marginLeft: 6, fontSize: 11, padding: "2px 6px", background: "#FEF3C7", color: "#92400E", borderRadius: 3, fontWeight: 600 }}
                    >
                      ⚠ mismatch
                    </span>
                  )}
                </td>
                <td className="num">{fmtInt(totalQty(p))}</td>
                <td>
                  <button className="btn btn-ghost btn-sm" data-testid={`restock-product-${p.style_id}`} title="Restock" onClick={() => setRestocking(p)}><PackagePlus size={14}/></button>
                  <button className="btn btn-ghost btn-sm" data-testid={`activity-log-${p.style_id}`} title="Activity Log" onClick={() => nav(`/products/${p.id}/activity`)}><Activity size={14}/></button>
                  <button className="btn btn-ghost btn-sm" data-testid={`edit-product-${p.style_id}`} onClick={() => { setErr(""); setEditing(p); }}><Pencil size={14}/></button>
                  <button className="btn btn-ghost btn-sm" data-testid={`delete-product-${p.style_id}`} onClick={() => remove(p.id)}><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} pageSize={pageSize} total={filtered.length} onPageChange={setPage} onPageSizeChange={setPageSize}/>
      </div>

      {editing && (
        <ProductForm
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          err={err} setErr={setErr}
        />
      )}
      {restocking && (
        <RestockModal
          product={restocking}
          onClose={() => setRestocking(null)}
          onSaved={() => { setRestocking(null); load(); }}
        />
      )}
    </div>
  );
}

function ProductForm({ product, onClose, onSaved, err, setErr }) {
  const [p, setP] = useState(product);
  const [busy, setBusy] = useState(false);
  const update = (k, v) => setP({ ...p, [k]: v });
  const addColor = () => setP({ ...p, colors: [...p.colors, { color: "", sizes: Object.fromEntries(SIZES.map((s) => [s, 0])) }] });
  const removeColor = (i) => setP({ ...p, colors: p.colors.filter((_, ix) => ix !== i) });
  const updateColor = (i, k, v) => setP({ ...p, colors: p.colors.map((c, ix) => ix === i ? { ...c, [k]: v } : c) });
  const updateSize = (i, s, v) => setP({ ...p, colors: p.colors.map((c, ix) => ix === i ? { ...c, sizes: { ...c.sizes, [s]: Number(v) || 0 } } : c) });

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/products/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setP((prev) => ({ ...prev, image_path: data.image_path }));
  };

  const save = async () => {
    setErr("");
    if (!p.style_id.trim() || !p.name.trim()) {
      setErr("Style ID and Product Name are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...p,
        style_id: p.style_id.trim(),
        name: p.name.trim(),
        cost_price: Number(p.cost_price) || 0,
        selling_price: Number(p.selling_price) || 0,
        cutting_quantity: p.cutting_quantity === "" || p.cutting_quantity === null || p.cutting_quantity === undefined
          ? null
          : Number(p.cutting_quantity),
      };
      if (p.id) await api.put(`/products/${p.id}`, payload);
      else await api.post("/products", payload);
      onSaved();
    } catch (e) {
      setErr(formatApiError(e));
      setP((prev) => ({
        ...prev,
        colors: product.colors.map((c) => ({ ...c, sizes: { ...c.sizes } })),
      }));
    }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="product-modal">
        <div className="modal-h">
          <h2>{p.id ? "Edit Product" : "Add Product"}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-b">
          {err && <div className="error-msg" data-testid="product-error" style={{ marginBottom: 14 }}>{err}</div>}
          <div className="grid-form" style={{ marginBottom: 14 }}>
            <div className="field"><label>Style ID *</label><input data-testid="prod-style" className="input" value={p.style_id} onChange={(e) => update("style_id", e.target.value)}/></div>
            <div className="field"><label>Product Name *</label><input data-testid="prod-name" className="input" value={p.name} onChange={(e) => update("name", e.target.value)}/></div>
            <div className="field"><label>Fabric</label><input data-testid="prod-fabric" className="input" value={p.fabric} onChange={(e) => update("fabric", e.target.value)}/></div>
            <div className="field"><label>Cost Price</label><input data-testid="prod-cost" className="input num" type="number" step="0.01" value={p.cost_price} onChange={(e) => update("cost_price", e.target.value)}/></div>
            <div className="field"><label>Selling Price</label><input data-testid="prod-selling" className="input num" type="number" step="0.01" value={p.selling_price} onChange={(e) => update("selling_price", e.target.value)}/></div>
            <div className="field">
              <label>Total Cutting Quantity <span style={{ fontWeight: 400, color: "var(--text-muted)", textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
              <input data-testid="prod-cutting-qty" className="input num" type="number" min="0" placeholder="e.g. 120" value={p.cutting_quantity ?? ""} onChange={(e) => update("cutting_quantity", e.target.value === "" ? "" : Number(e.target.value))}/>
            </div>
          </div>

          <div className="field" style={{ marginBottom: 14 }}>
            <label>Product Image</label>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              {p.image_path && <img className="thumb-big" src={resolveImage(p.image_path)} alt=""/>}
              <input data-testid="prod-image" type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}/>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600 }}>Color Variants & Stock</h3>
            {!p.id && (
              <button className="btn btn-secondary btn-sm" data-testid="add-color-btn" onClick={addColor}><Plus size={14}/> Add Color</button>
            )}
          </div>

          {p.id && (
            <div style={{ padding: "8px 12px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 4, fontSize: 12.5, color: "#92400E", marginBottom: 12 }}>
              Stock quantities are read-only here. Use <strong>Restock</strong> to add stock, or create an invoice to reduce it.
            </div>
          )}

          {p.colors.length === 0 && <p style={{ color: "var(--text-muted)", fontSize: 13 }}>No color variants yet.</p>}
          {p.colors.map((c, i) => (
            <div key={i} className="variant-row" data-testid={`variant-${i}`}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 10 }}>
                <div className="field" style={{ flex: 1 }}>
                  <label>Color</label>
                  {p.id ? (
                    <div style={{ padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 4, background: "#F8FAFC", fontWeight: 600 }}>{c.color}</div>
                  ) : (
                    <input data-testid={`variant-color-${i}`} className="input" value={c.color} onChange={(e) => updateColor(i, "color", e.target.value)} placeholder="e.g. Pink"/>
                  )}
                </div>
                {!p.id && (
                  <button className="btn btn-danger btn-sm" data-testid={`remove-color-${i}`} onClick={() => removeColor(i)}><Trash2 size={14}/></button>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${SIZES.length}, 1fr)`, gap: 8 }}>
                {SIZES.map((s) => (
                  <div key={s} className="field">
                    <label>Size {s}</label>
                    {p.id ? (
                      <div style={{ padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 4, background: "#F8FAFC", textAlign: "center" }} className="tabular">{c.sizes[s] ?? 0}</div>
                    ) : (
                      <input data-testid={`variant-${i}-size-${s}`} className="input num" type="number" min="0" value={c.sizes[s] ?? 0} onChange={(e) => updateSize(i, s, e.target.value)}/>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="modal-f">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" data-testid="save-product-btn" disabled={busy} onClick={save}>{busy ? "Saving..." : "Save Product"}</button>
        </div>
        {err && (
          <div
            data-testid="product-error-bottom"
            style={{
              margin: "0 20px 20px",
              padding: "12px 16px",
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              color: "#B91C1C",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {err}
          </div>
        )}
      </div>
    </div>
  );
}

function RestockModal({ product, onClose, onSaved }) {
  const [liveProduct, setLiveProduct] = useState(product);
  const [colors, setColors] = useState(
    product.colors.map((c) => ({ color: c.color, sizes: Object.fromEntries(SIZES.map((s) => [s, 0])), existing: true, currentSizes: c.sizes }))
  );
  const [note, setNote] = useState("");
  const [cuttingQtyAdded, setCuttingQtyAdded] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirming, setConfirming] = useState(false);

  // Refetch the product fresh when the modal opens, so "current stock" shown
  // is guaranteed accurate even if the list was loaded a while ago.
  useEffect(() => {
    api.get(`/products/${product.id}`).then(({ data }) => {
      setLiveProduct(data);
      setColors((prev) =>
        prev.map((c) => {
          const fresh = data.colors.find((fc) => fc.color === c.color);
          return fresh ? { ...c, currentSizes: fresh.sizes } : c;
        })
      );
    });
  }, [product.id]);

  const loadHistory = () => api.get(`/products/${product.id}/restocks`).then(({ data }) => setHistory(data));

  const updateColorSize = (i, s, v) => setColors((prev) => prev.map((c, ix) => ix === i ? { ...c, sizes: { ...c.sizes, [s]: Math.max(0, Number(v) || 0) } } : c));
  const updateColorName = (i, name) => setColors((prev) => prev.map((c, ix) => ix === i ? { ...c, color: name } : c));
  const addNewColorRow = () => setColors((prev) => [...prev, { color: "", sizes: Object.fromEntries(SIZES.map((s) => [s, 0])), existing: false, currentSizes: {} }]);
  const removeColorRow = (i) => setColors((prev) => prev.filter((_, ix) => ix !== i));

  const totalAdding = colors.reduce((sum, c) => sum + SIZES.reduce((a, s) => a + (Number(c.sizes[s]) || 0), 0), 0);
  const incompleteNewColor = colors.some((c) => !c.existing && !c.color.trim() && SIZES.some((s) => Number(c.sizes[s]) > 0));

  const rowsWithChanges = colors.filter((c) => c.color.trim() && SIZES.some((s) => Number(c.sizes[s]) > 0));

  const attemptSave = () => {
    setErr("");
    const blankNamed = colors.find((c) => !c.existing && !c.color.trim() && SIZES.some((s) => Number(c.sizes[s]) > 0));
    if (blankNamed) {
      setErr("One of your new color rows has quantities but no color name. Please name it or remove the row.");
      return;
    }
    if (totalAdding === 0) {
      setErr("Enter at least one quantity to add.");
      return;
    }
    setConfirming(true);
  };

  const confirmSave = async () => {
    setBusy(true); setErr("");
    try {
      const items = colors
        .filter((c) => c.color.trim())
        .map((c) => ({ color: c.color.trim(), sizes: c.sizes }))
        .filter((it) => SIZES.some((s) => Number(it.sizes[s]) > 0));
      await api.post("/restocks", {
        product_id: product.id,
        items,
        cutting_quantity_added: cuttingQtyAdded === "" ? null : Number(cuttingQtyAdded),
        note: note.trim(),
      });
      onSaved();
    } catch (e) {
      setErr(formatApiError(e));
      setConfirming(false);
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="restock-modal" style={{ maxWidth: 680 }}>
        <div className="modal-h">
          <h2>Restock — {product.style_id}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-b">
          {err && <div className="error-msg" data-testid="restock-error" style={{ marginBottom: 14 }}>{err}</div>}

          {!confirming ? (
            <>
              <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 14 }}>
                Enter the quantity being <strong>added</strong> for each size. Current stock is shown for reference. This only adds — it never removes or overwrites.
              </p>

              {colors.map((c, i) => (
                <div key={i} className="variant-row" data-testid={`restock-color-${i}`} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 10 }}>
                    {c.existing ? (
                      <strong>{c.color}</strong>
                    ) : (
                      <>
                        <input
                          data-testid={`restock-new-color-name-${i}`}
                          className="input"
                          placeholder="New color name (e.g. Maroon)"
                          value={c.color}
                          onChange={(e) => updateColorName(i, e.target.value)}
                          style={{ flex: 1, fontWeight: 600 }}
                        />
                        <span className="badge badge-mute">New Color</span>
                      </>
                    )}
                    {!c.existing && <button className="btn btn-ghost btn-sm" onClick={() => removeColorRow(i)} title="Remove this new color row"><X size={14}/></button>}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(${SIZES.length}, 1fr)`, gap: 8 }}>
                    {SIZES.map((s) => {
                      const current = Number(c.currentSizes?.[s] || 0);
                      const adding = Number(c.sizes[s] || 0);
                      return (
                        <div key={s} className="field">
                          <label>
                            Size {s}
                            <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: 10, textTransform: "none", letterSpacing: 0 }}>
                              {" "}· has {current}
                            </span>
                          </label>
                          <input
                            data-testid={`restock-${i}-size-${s}`}
                            className="input num"
                            type="number"
                            min="0"
                            placeholder="+0"
                            value={c.sizes[s] || ""}
                            onChange={(e) => updateColorSize(i, s, e.target.value)}
                          />
                          {adding > 0 && (
                            <div style={{ fontSize: 11, color: "#15803D", marginTop: 3, fontWeight: 600 }}>
                              → becomes {current + adding}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <button className="btn btn-secondary btn-sm" data-testid="restock-add-new-color-btn" onClick={addNewColorRow} style={{ marginBottom: 4 }}>
                <Plus size={14}/> Add New Color
              </button>

              <div className="field" style={{ marginTop: 14 }}>
                <label>Cutting Quantity Added <span style={{ fontWeight: 400, color: "var(--text-muted)", textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                <input data-testid="restock-cutting-qty" className="input num" type="number" min="0" placeholder="e.g. 20, if new fabric was cut for this lot" value={cuttingQtyAdded} onChange={(e) => setCuttingQtyAdded(e.target.value)}/>
              </div>
              <div className="field" style={{ marginTop: 14 }}>
                <label>Note (optional)</label>
                <input data-testid="restock-note" className="input" placeholder="e.g. 2nd lot from tailor" value={note} onChange={(e) => setNote(e.target.value)}/>
              </div>

              {totalAdding > 0 && (
                <div style={{ marginTop: 12, padding: "10px 14px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 4, fontSize: 13, color: "#15803D" }}>
                  You are adding <strong>{totalAdding}</strong> total pieces in this restock.
                </div>
              )}
              {incompleteNewColor && (
                <div style={{ marginTop: 10, padding: "10px 14px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 4, fontSize: 13, color: "#92400E" }}>
                  A new color row has quantities entered but no name yet — give it a name before saving, or it won't be counted.
                </div>
              )}

              <div style={{ marginTop: 18 }}>
                <button
                  className="btn btn-ghost btn-sm"
                  data-testid="toggle-restock-history"
                  onClick={() => { setShowHistory((s) => !s); if (!showHistory) loadHistory(); }}
                >
                  <History size={14}/> {showHistory ? "Hide" : "View"} Restock History
                </button>
                {showHistory && (
                  <div style={{ marginTop: 10, maxHeight: 220, overflowY: "auto" }} data-testid="restock-history">
                    {history.length === 0 && <p style={{ color: "var(--text-muted)", fontSize: 13 }}>No restocks logged yet.</p>}
                    {history.map((r) => {
                      const restockTotal = r.items.reduce(
                        (sum, it) => sum + Object.values(it.sizes).reduce((a, q) => a + Number(q || 0), 0),
                        0
                      );
                      return (
                        <div key={r.id} style={{ padding: "8px 0", borderBottom: "1px solid var(--border)", fontSize: 13 }}>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <strong>
                              {new Date(r.created_at).toLocaleDateString()}{" "}
                              <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>
                                {new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </strong>
                            {r.note && <span style={{ color: "var(--text-muted)" }}>{r.note}</span>}
                          </div>
                          <div style={{ color: "var(--text)", fontWeight: 600, marginTop: 4 }}>
                            Total Quantity = {restockTotal}
                          </div>
                          {r.items.map((it, ix) => (
                            <div key={ix} style={{ color: "var(--text-muted)", marginTop: 2 }}>
                              {it.color}: {Object.entries(it.sizes).map(([s, q]) => `${s}+${q}`).join(", ")}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <p style={{ fontSize: 14, marginBottom: 14 }}>
                Please confirm — this restock will make these changes:
              </p>
              <div style={{ border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
                {rowsWithChanges.map((c, i) => (
                  <div key={i} style={{ padding: "10px 14px", borderBottom: i < rowsWithChanges.length - 1 ? "1px solid var(--border)" : "none", background: i % 2 ? "#FBFCFD" : "white" }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>
                      {c.color} {!c.existing && <span className="badge badge-mute" style={{ marginLeft: 6 }}>New Color</span>}
                    </div>
                    {SIZES.filter((s) => Number(c.sizes[s]) > 0).map((s) => {
                      const current = Number(c.currentSizes?.[s] || 0);
                      const adding = Number(c.sizes[s] || 0);
                      return (
                        <div key={s} style={{ fontSize: 13, color: "var(--text-muted)" }}>
                          Size {s}: {current} + {adding} = <strong style={{ color: "var(--text)" }}>{current + adding}</strong>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 14, padding: "10px 14px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 4, fontSize: 14, color: "#15803D", fontWeight: 600 }}>
                Total pieces being added: {totalAdding}
              </div>
              {note.trim() && (
                <p style={{ marginTop: 10, fontSize: 13, color: "var(--text-muted)" }}>Note: {note.trim()}</p>
              )}
            </>
          )}
        </div>
        <div className="modal-f">
          {!confirming ? (
            <>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" data-testid="save-restock-btn" disabled={totalAdding === 0} onClick={attemptSave}>
                Review & Save
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={() => setConfirming(false)} disabled={busy}>Go Back</button>
              <button className="btn btn-primary" data-testid="confirm-restock-btn" disabled={busy} onClick={confirmSave}>
                {busy ? "Saving..." : "Confirm & Save Restock"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}